import moment from 'moment';
import {LeanDocument} from 'mongoose';
import {TimetableItem} from '@/interfaces/timetable/TimetableItem';
import {TimetableItemTypes} from '@/enums/timetable/TimetableItemTypes';
import {CourseStates} from '@/enums/CourseStates.enum';
import {TimetableCancellationReason} from '@/enums/scheduler/TimetableCancellationReason.enum';
import {CourseLogEntryDocument} from '@/documentTypes/recorder/discriminator/CourseLogEntry.discriminator';
import {SubstitutionInquiryDocument} from '@/documentTypes/substitution-manager/SubstitutionInquiry.document';
import {isSameOccurrenceDay, parseEventDate, participationEndedText} from '@/helpers/timetable/timetableOccurrence.helper';
import {deriveLessonStatus, logStatusInfo} from '@/helpers/timetable2/lessonStatus';
import {InquiryReasonFields, reasonLabel, ReasonEntry} from '@/helpers/timetable2/cancellationReason';
import {moveBadgeText, moveInfoOf} from '@/helpers/timetable2/lessonMove';
import {makeupInfoOf} from '@/helpers/makeup/makeupLesson';
import {
    CourseTimetableItem,
    DAY_FORMAT,
    HandoutVM,
    HolidayVM,
    InquiryRef,
    LessonVM,
    LogState,
} from '@/helpers/timetable2/types';

/**
 * Scheduler items -> view models (pure). Course logs are matched to an occurrence by DAY
 * (same rule as the Today view: customId + student + payload.instance on the start day).
 */

export type CourseLog = LeanDocument<CourseLogEntryDocument>;
export type LeanInquiry = LeanDocument<SubstitutionInquiryDocument>;

/** String id of an ObjectId, populated document or string ('' if missing) */
export function idOf(value: unknown): string {
    if (!value) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    const doc = value as { _id?: unknown; toHexString?: () => string };
    if (doc._id !== undefined && doc._id !== value) {
        return idOf(doc._id);
    }
    return typeof doc.toHexString === 'function' ? doc.toHexString() : String(value);
}

function populated<T extends object>(value: unknown): T | undefined {
    return value && typeof value === 'object' && '_id' in (value as object) && Object.keys(value as object).length > 1
        ? value as T : undefined;
}

export function personName(value: unknown): string {
    const doc = populated<{ firstName?: string; lastName?: string; name?: string }>(value);
    if (!doc) {
        return '';
    }
    return doc.name || `${doc.firstName || ''} ${doc.lastName || ''}`.trim();
}

/** "M. Klaczinski" */
export function shortPersonName(value: unknown): string {
    const doc = populated<{ firstName?: string; lastName?: string }>(value);
    if (!doc) {
        return '';
    }
    const initial = doc.firstName ? `${doc.firstName.trim().charAt(0)}. ` : '';
    return `${initial}${doc.lastName || ''}`.trim();
}

/** Same room text as the Today view */
export function roomText(value: unknown): string {
    const room = populated<{ name?: string; roomNumber?: string }>(value);
    if (!room) {
        return '';
    }
    if (room.name) {
        return room.roomNumber ? `${room.name} (${room.roomNumber})` : room.name;
    }
    return room.roomNumber ? `Raum ${room.roomNumber}` : '';
}

export function lessonKey(customId: string, start: string): string {
    return `${customId}|${start}`;
}

function minutesOfDay(value: moment.Moment): number {
    return value.hours() * 60 + value.minutes();
}

/** customId -> logs, for fast matching */
export function indexLogs(logs: CourseLog[]): Map<string, CourseLog[]> {
    const index = new Map<string, CourseLog[]>();
    for (const log of logs) {
        const customId = log.payload?.customId;
        if (!customId) {
            continue;
        }
        const list = index.get(customId) || [];
        list.push(log);
        index.set(customId, list);
    }
    return index;
}

/** Log of exactly this occurrence for the student (Today rule: same customId, student and day) */
export function findOccurrenceLog(logs: CourseLog[] | undefined, customId: string, studentId: string, start: string): CourseLog | undefined {
    return (logs || []).find((log) => log.payload?.customId === customId
        && idOf(log.payload.student || log.object) === studentId
        && isSameOccurrenceDay(log.payload.instance, start));
}

/**
 * Inquiry data of the calendar. The reason label ("Krankheit – Notiz") comes from B's cancellationReason helper:
 * the reason id is looked up in the (session-cached) taxonomy entries, populated documents work as well.
 */
export function toInquiryRef(inquiry: LeanInquiry, reasonEntries: ReasonEntry[] = []): InquiryRef {
    const config = inquiry.cancellationConfig;
    const label = reasonLabel(inquiry as unknown as InquiryReasonFields, reasonEntries);
    return {
        id: idOf(inquiry._id),
        state: inquiry.state,
        cancellationConfigured: !!config,
        cancellationSendAt: config?.sendAt ? moment(config.sendAt).format('YYYY-MM-DD HH:mm') : undefined,
        cancellationReasonText: label || undefined,
    };
}

/** Whether any inquiry references a reason entry by id (only then the taxonomy list is needed) */
export function needsReasonEntries(inquiries: LeanInquiry[]): boolean {
    return inquiries.some((inquiry) => {
        const reason = (inquiry as unknown as InquiryReasonFields).cancellationReason;
        return !!reason && !(typeof reason === 'object' && typeof (reason as { name?: unknown }).name === 'string');
    });
}

export interface OccurrenceContext {
    now: number;
    logsByCustomId: Map<string, CourseLog[]>;
    inquiries: Map<string, InquiryRef>;
}

function computeLogState(kindTakesPlace: boolean, isDraft: boolean, endMs: number, now: number, expected: number, logged: number): LogState {
    if (!kindTakesPlace || isDraft) {
        return 'notExpected';
    }
    if (endMs > now) {
        return 'upcoming';
    }
    if (expected === 0) {
        return 'noStudents';
    }
    if (logged >= expected) {
        return 'complete';
    }
    return logged > 0 ? 'partial' : 'missing';
}

/** View model of one course occurrence (conflicts are added afterwards by conflicts.ts) */
export function toLessonVM(item: CourseTimetableItem, ctx: OccurrenceContext): LessonVM {
    const course = item.data;
    const start = parseEventDate(item.start);
    const end = parseEventDate(item.end || item.start);
    const startText = start.format('YYYY-MM-DD HH:mm');
    const customId = course?.customId || '';
    const day = start.clone().startOf('day');
    const participating = (course?.studentItems || []).filter((studentItem) => !participationEndedText(studentItem, day));
    const withId = participating.filter((s) => !!idOf(s.student));
    const studentIds = withId.map((s) => idOf(s.student));
    const inquiryId = idOf(course?.substitutionInquiry);
    const inquiry = inquiryId ? ctx.inquiries.get(inquiryId) : undefined;
    const isDraft = course?.courseState === CourseStates.DRAFT;
    // "Stunde einmalig verschieben": 'moved' is a regular lesson at the new date, 'origin' a ghost (never a lesson)
    const move = moveInfoOf(item);
    const moveText = moveBadgeText(move);
    const status = deriveLessonStatus({
        isMovedOrigin: move?.role === 'origin' || item.cancellationReason === TimetableCancellationReason.MOVED,
        movedText: move?.role === 'origin' ? moveText : undefined,
        isCanceled: item.isCanceled,
        isVacant: item.isVacant,
        isSubstituted: item.isSubstituted,
        isDraft,
        inquiry,
        cancellationReasonText: inquiry?.cancellationReasonText,
    });
    const takesPlace = status.kind === 'regular' || status.kind === 'substituted';
    const logs = ctx.logsByCustomId.get(customId);
    const loggedCount = takesPlace ? studentIds.filter((id) => !!findOccurrenceLog(logs, customId, id, startText)).length : 0;
    const startMs = start.valueOf();
    const endMs = end.valueOf();
    const logState = computeLogState(takesPlace, isDraft, endMs, ctx.now, studentIds.length, loggedCount);
    const logInfo = logStatusInfo(logState, loggedCount, studentIds.length);
    return {
        key: lessonKey(customId, startText),
        item,
        courseId: idOf(course?._id || item._id),
        customId,
        start: startText,
        end: end.format('YYYY-MM-DD HH:mm'),
        day: day.format(DAY_FORMAT),
        dayIndex: start.isoWeekday() - 1,
        startMinutes: minutesOfDay(start),
        endMinutes: end.isSame(start, 'day') ? minutesOfDay(end) : 24 * 60,
        startMs,
        endMs,
        subjectId: idOf(course?.subject),
        subjectName: (populated<{ name?: string }>(course?.subject))?.name || '',
        color: item.color || 'blue-grey',
        teacherId: idOf(course?.teacher),
        teacherName: personName(course?.teacher),
        owningTeacherId: idOf(course?.owningTeacher),
        owningTeacherName: personName(course?.owningTeacher),
        roomId: idOf(course?.room),
        roomName: roomText(course?.room),
        institutionId: idOf(course?.institution),
        institutionName: (populated<{ name?: string }>(course?.institution))?.name || '',
        studentIds,
        /** Same order as studentIds ('' if not populated) */
        studentNames: withId.map((s) => personName(s.student)),
        participantCount: studentIds.length,
        weekInterval: course?.weekInterval || 1,
        isDraft,
        isOpenForRegistration: !!course?.isOpenForRegistration,
        isPast: endMs <= ctx.now,
        isRunning: startMs <= ctx.now && endMs > ctx.now,
        status,
        inquiry,
        logState,
        loggedCount,
        expectedLogCount: studentIds.length,
        conflicts: [],
        hasProblem: status.problem || !!logInfo?.problem || (move?.role === 'moved' && !!move.teacherDecisionPending),
        ...(move ? {moveRole: move.role, moveText} : {}),
        ...(move?.role === 'moved' && move.teacherDecisionPending ? {teacherDecisionPending: true} : {}),
        ...(course?.makeup ? {makeup: makeupInfoOf(course.makeup)} : {}),
    };
}

export function toHolidayVM(item: TimetableItem<any>): HolidayVM {
    const start = parseEventDate(item.start);
    const end = parseEventDate(item.end || item.start);
    return {
        key: `holiday|${idOf(item._id) || item.name}|${start.format(DAY_FORMAT)}`,
        name: item.name,
        startDay: start.format(DAY_FORMAT),
        endDay: end.format(DAY_FORMAT),
        item,
    };
}

export function toHandoutVM(item: TimetableItem<any>): HandoutVM {
    const start = parseEventDate(item.start);
    return {
        key: `handout|${idOf(item._id)}|${start.format('YYYY-MM-DD HH:mm')}`,
        start: start.format('YYYY-MM-DD HH:mm'),
        end: parseEventDate(item.end || item.start).format('YYYY-MM-DD HH:mm'),
        day: start.format(DAY_FORMAT),
        item,
    };
}

export interface BuiltModel {
    lessons: LessonVM[];
    holidays: HolidayVM[];
    handouts: HandoutVM[];
}

/** Whether the item overlaps the visible days (scheduler DAY/MONTH may return out-of-range items) */
function inRange(item: TimetableItem<any>, from: string, to: string): boolean {
    const start = parseEventDate(item.start).format(DAY_FORMAT);
    const end = parseEventDate(item.end || item.start).format(DAY_FORMAT);
    return end >= from && start <= to;
}

/** Splits scheduler items into lessons, holidays and handouts within [from, to] (days, inclusive) */
export function buildModel(events: TimetableItem<any>[], from: string, to: string, ctx: OccurrenceContext): BuiltModel {
    const lessons: LessonVM[] = [];
    const holidays: HolidayVM[] = [];
    const handouts: HandoutVM[] = [];
    const seen = new Set<string>();
    for (const item of events) {
        if (!item || !inRange(item, from, to)) {
            continue;
        }
        if (item.type === TimetableItemTypes.COURSE && item.data) {
            const vm = toLessonVM(item as CourseTimetableItem, ctx);
            if (!seen.has(vm.key)) {
                seen.add(vm.key);
                lessons.push(vm);
            }
        } else if (item.type === TimetableItemTypes.HOLIDAY) {
            holidays.push(toHolidayVM(item));
        } else if (item.type === TimetableItemTypes.HANDOUTEVENT) {
            handouts.push(toHandoutVM(item));
        }
    }
    lessons.sort((a, b) => a.startMs - b.startMs || a.customId.localeCompare(b.customId));
    return {lessons, holidays, handouts};
}

/** Ids of substitution inquiries referenced by vacant or substituted lessons (one batch request) */
export function inquiryIdsOf(events: TimetableItem<any>[]): string[] {
    const ids = new Set<string>();
    for (const item of events) {
        if (item?.type === TimetableItemTypes.COURSE && item.lessonMove?.role !== 'origin' && (item.isVacant || item.isSubstituted)) {
            const id = idOf(item.data?.substitutionInquiry);
            if (id) {
                ids.add(id);
            }
        }
    }
    return [...ids];
}

/** Course items whose logs are relevant (ended before `now`, taking place) */
export function loggableCourseItems(events: TimetableItem<any>[], now: number): CourseTimetableItem[] {
    // Ghosts of moved lessons are isCanceled (MOVED) and therefore never loggable
    return events.filter((item) => item?.type === TimetableItemTypes.COURSE && !item.isCanceled && !item.isVacant
        && item.lessonMove?.role !== 'origin'
        && item.data?.courseState !== CourseStates.DRAFT
        && parseEventDate(item.start).valueOf() <= now) as CourseTimetableItem[];
}
